import React, { useCallback } from 'react';
import { TSession, determineState, State } from './Sessions';
import { withRouter, RouteComponentProps } from 'react-router';
import { CheckCircle, Input, HelpOutline } from '@material-ui/icons';
import { LinkDiv, StyledError } from './SessionPrepareButton';
import { CircularProgressProps, Box, CircularProgress, Typography, Tooltip } from '@material-ui/core';
import { VideoTranscodeState } from '../../api';
import { transcodeState } from '../../services/sessionsUpload';
import useSessionJobs, { isActive, toPercent } from './useSessionJobs';

interface Props extends RouteComponentProps {
  session: TSession;
}

export function CircularProgressWithLabel(props: CircularProgressProps & { value: number }) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="static" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
         <Typography variant="caption" component="div" color="textSecondary">{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

const SessionUploadButton: React.FunctionComponent<Props> = ({ session, history }) => {
  const state = determineState(session);
  const transcodes = useSessionJobs(String(session.id)).filter((job) => job.type === 'transcode' && isActive(job));
  const transcodePercent = transcodes.length > 0
    ? transcodes.reduce((sum, job) => sum + toPercent(job), 0) / transcodes.length
    : undefined;

  const onClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      history.push('/session/upload/' + session.id);
    },
    [session, history]
  );

  const onTranscodeClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      history.push('/session/transcode/' + session.id);
    },
    [session, history]
  );

  if (state === State.Upload || state === State.Prepare) {
    //In these states we can still edit the videos
    let child;
    if (!session.videos || session.videos.length === 0) {
      //Not all videos have been uploaded yet
      child = <Input color="secondary" />;
    } else {
      let errors = session.videos
        .filter((video) => transcodeState(video) === VideoTranscodeState.Failed)
        .map(
          (video) =>
            'Failed to transcode ' +
            video.url +
            (video.transcodeStatus?.error ? ' (' + video.transcodeStatus?.error + ')' : '')
        );
      if (errors.length > 0) {
        child = (
          <Tooltip title={errors.reduce((prev, cur) => prev + '\n' + cur)}>
            <StyledError onClick={onTranscodeClick} />
          </Tooltip>
        );
      } else {
        const running = session.videos.filter((v) => transcodeState(v) === VideoTranscodeState.Running || transcodeState(v) === VideoTranscodeState.Copying).length;
        const ok = session.videos.filter((v) => transcodeState(v) === VideoTranscodeState.Succeeded).length;
        if (running === 0 && ok === session.videos.length) {
          child = (
            <Tooltip title={session.videos.length + ' videos have been uploaded and are available'}>
              <CheckCircle color="secondary" />
            </Tooltip>
          );
        } else if (running > 0) {
          child = (
            <Tooltip title={`Transcoding ${running} of ${session.videos.length} videos, click for progress`}>
              {transcodePercent !== undefined ? (
                <CircularProgressWithLabel color="secondary" value={transcodePercent} onClick={onTranscodeClick} />
              ) : (
                <CircularProgress color="secondary" onClick={onTranscodeClick} />
              )}
            </Tooltip>
          );
        } else {
          child = (
            <Tooltip title="Either transcoding is starting or session is too old">
              <HelpOutline color="secondary" />
            </Tooltip>
          );
        }
      }
    }
    return <LinkDiv onClick={onClick}>{child}</LinkDiv>;
  }
  return (
    <LinkDiv onClick={onClick}>
      <Tooltip title="All videos have been uploaded, you can not change them anymore">
        <CheckCircle color="secondary" />
      </Tooltip>
    </LinkDiv>
  );
};

export default withRouter(SessionUploadButton);
